/**
 * Renders a FixedSideBar that contains an image,
 * Suite sections, and a button
 */
/** @jsx jsx */
import PropTypes from 'prop-types';
import { css, jsx } from '@emotion/core';
import { useStaticQuery, graphql } from 'gatsby';
import Image from '../../atoms/Image/Image';
import sanityImage from '../../../utils/sanityImage';
import Button from '../../atoms/Buttons/Button/Button';
import Card from '../../atoms/Card/Card';
import { Heading4 } from '../../atoms/Headings/Headings';
import {
  breakpoints,
  colors,
  pageDimensions,
  shadows,
  unitConverter as uc,
} from '../../../styles/base';

import SuiteSection from '../Menu/MenuSection/SuiteSection/SuiteSection';
import BlockContentSection from '../../molecules/BlockContentSection/BlockContentSection';

// render list of suites from the main menu
function renderProductList() {
  // query for getting Menu Section information
  const query = graphql`
    {
      sanityMenu(menuName: { eq: "Main Menu" }) {
        _rawMenuDrawers(resolveReferences: { maxDepth: 10 })
      }
    }
  `;

  const { sanityMenu } = useStaticQuery(query);
  const products =
    sanityMenu._rawMenuDrawers[0].menuSections[0].suiteSectionItems;

  const arrayToObject = (array, keyField) =>
    array.reduce((obj, item) => {
      const arrayList = obj;
      arrayList[item[keyField]] = item;
      return arrayList;
    }, {});

  const suiteList = arrayToObject(products, 'suiteType');

  return suiteList;
}

// render what content will be displayed in the sidebar
function renderFixedSideBar(content, type) {
  if (type === 'clientFixedSideBar') {
    // override white background from suitesection
    const productCategorySectionCss = css`
      margin-bottom: ${uc('25px')};

      div {
        background: ${colors.lightGray.six};

        h2,
        p {
          width: 100%;
        }
      }

      div:nth-of-type(2) {
        width: 85%;
      }

      @media (${breakpoints.largeTablet}) {
        > div {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-around;
        }

        div h2,
        div p {
          display: inline-block;
          white-space: normal !important;
          word-wrap: break-word;
        }

        .icon-link-subheading {
          flex-wrap: wrap;
          width: 50%;
          padding: 0;
          text-align: center;

          &:first-of-type {
            padding-top: 0 !important;
          }

          &:last-of-type {
            padding-bottom: 0 !important;
          }

          div {
            width: auto;
            height: auto;
          }
        }
      }

      @media (${breakpoints.tablet}) {
        .icon-link-subheading {
          width: 50%;
        }
      }

      @media (${breakpoints.mobile}) {
        width: 95%;
        padding-left: 0;

        .icon-link-subheading {
          flex-wrap: nowrap;
          justify-content: space-between;
          width: 100%;
          padding: ${uc('20px 0 0 0')};
          text-align: left;

          div {
            width: auto;

            h2 {
              width: auto;
            }
          }
        }
      }
    `;
    // query for menu products
    const productList = renderProductList();
    const products = content.productsUsed.map(product => productList[product]);
    return (
      <div className="suites-used" css={productCategorySectionCss}>
        <SuiteSection suiteSectionItems={products} />
      </div>
    );
  }

  if (type === 'industryFixedSideBar') {
    const logosCss = css`
      display: flex; /* for IE11 */
      display: grid;
      flex-wrap: wrap;
      grid-column-gap: ${uc('50px')};
      grid-template-columns: repeat(2, 1fr);
      justify-content: space-between;
      width: ${uc('300px')};
      margin: ${uc('25px auto 0')};
      padding: ${uc('20px 0')};

      img {
        flex: 1 0 50%;
        align-self: center;
        justify-self: center;
        max-height: ${uc('60px')};
        margin-bottom: ${uc('25px')};
      }
    `;
    return (
      <div css={logosCss}>
        {content.logos.map(logo => (
          <Image
            src={sanityImage(logo)
              .height(60)
              .fit('max')
              .url()}
            key={`logo-component-${logo._key}`}
            alt="company logo"
            title="company logo"
          />
        ))}
      </div>
    );
  }

  if (type === 'subscriptionFixedSideBar') {
    const blockContentCss = css`
      margin: ${uc('20px 0 0 20px')};
      text-align: left;

      ${Heading4} {
        font-size: ${uc('17px')};

        &:nth-of-type(1) {
          color: ${colors.marigold};
        }

        &:nth-of-type(2) {
          color: ${colors.accent};
        }

        &:nth-of-type(3) {
          margin-bottom: ${uc('10px')};
          color: ${colors.mint};
        }
      }

      li {
        margin: ${uc('0 0 2px 20px')};
      }
    `;

    return (
      <div css={blockContentCss}>
        <BlockContentSection {...content.text} inSidebar />
      </div>
    );
  }

  return null;
}

/**
 * The <FixedSideBar /> Component
 */

const FixedSideBar = ({
  _type,
  align,
  button,
  headingText,
  logo,
  ...props
}) => {
  const industryPage = _type === 'industryFixedSideBar';

  const fixedSideBarCss = css`
    position: absolute;
    top: ${uc('220px')};
    right: ${uc('45px')};
    z-index: 3;
    width: ${uc('437px')};
    padding-bottom: ${uc('50px')};
    text-align: center;
    background: ${colors.lightGray.six};

    div > .icon-link-subheading {
      padding-bottom: 0;
      text-align: left;

      p {
        white-space: normal;
      }
    }

    @media (max-width: ${pageDimensions.smallDesktopWidth}) {
      top: ${uc('-50px')};
    }

    @media (${breakpoints.largeTablet}) {
      position: relative;
      top: 0;
      right: auto;
      width: 95%;
      margin: ${uc('0 auto 40px')};
      margin-top: ${uc('20px')};
      padding: ${uc('1px 0')}; /* fix padding bug where it disappears */
    }
  `;

  const sidebarContentCss = css`
    margin: ${uc('40px auto')};
    margin-bottom: ${_type === 'subscriptionFixedSideBar' ? uc('0px') : 'auto'};
    padding: ${uc('0 20px')};
    border-radius: 25%;
  `;

  const logoCss = css`
    max-height: ${uc('196px')};
    margin-bottom: ${industryPage ? uc('10px') : uc('40px')};
    border-radius: 50%;
    box-shadow: ${shadows.large};
  `;

  const heading4Css = css`
    margin-bottom: ${industryPage ? 0 : uc('-10px')};
    margin-left: ${align === 'left' ? uc('30px') : '0'};
    text-align: ${align};

    @media (${breakpoints.largeTablet}) {
      margin: ${uc('0 0 20px 0')};
      text-align: center;
    }
  `;
  return (
    <Card css={fixedSideBarCss} id="fixed-sidebar">
      <div css={sidebarContentCss}>
        <Image
          css={logoCss}
          src={sanityImage(logo)
            .height(196)
            .fit('max')
            .url()}
          alt="logo"
        />
        <Heading4 css={heading4Css}>{headingText}</Heading4>
        {renderFixedSideBar(props, _type)}
        {button.text && <Button {...button}>{button.text}</Button>}
      </div>
    </Card>
  );
};

/**
 * Prop types
 */
FixedSideBar.propTypes = {
  align: PropTypes.string,
  button: PropTypes.instanceOf(Object),
  children: PropTypes.node,
  data: PropTypes.shape({}),
  headingText: PropTypes.string,
  logo: PropTypes.shape({}),
  suiteSection: PropTypes.shape({}),
  _type: PropTypes.string,
};

/**
 * Default props
 */
FixedSideBar.defaultProps = {
  align: 'left',
  button: {},
  children: null,
  data: {},
  headingText: 'Products used',
  logo: {},
  suiteSection: {},
  _type: '',
};

export default FixedSideBar;
